import { computed } from "vue";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import { useLazyServices } from "@/composables/use-lazy-services";
import {
  GATING_STRATEGY_REGISTRY,
  type GatingStrategyConfig,
} from "../strategies/gating-strategy-registry";
import {
  GatingStrategyType,
  type GatingStrategy,
} from "../strategies/gating-strategy";

/**
 * Handles lazy loading of gating strategies based on channel capabilities.
 *
 * INTERNAL: Use useGatingEvaluator() instead of importing this directly.
 */
export function useGatingStrategies() {
  const {
    canUseMembershipGating,
    canUseTokenGating,
    canUsePointsGating,
    canUseProductsGating,
  } = useChannelCapabilities();

  // Derive config from capabilities
  const strategyConfig = computed(
    (): GatingStrategyConfig => ({
      [GatingStrategyType.membership]: canUseMembershipGating.value,
      [GatingStrategyType.token]: canUseTokenGating.value,
      [GatingStrategyType.points]: canUsePointsGating.value,
      [GatingStrategyType.product]: canUseProductsGating.value,
    }),
  );

  // Lazy load strategies
  const { services, isReady } = useLazyServices<
    keyof GatingStrategyConfig,
    GatingStrategyConfig,
    Record<keyof GatingStrategyConfig, GatingStrategy>,
    undefined
  >({
    config: strategyConfig,
    definitions: GATING_STRATEGY_REGISTRY,
    deps: undefined,
  });

  // Flatten to array for iteration
  const strategies = computed(
    () => Object.values(services.value) as GatingStrategy[],
  );

  return {
    strategies,
    isReady,
  };
}
